import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ListEnvelope } from '../../../shared/api/genexus-query';
import type { CrudApi, CrudHooks } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import { createEmployeeFeriasApi } from './api';
import type { EmployeeFeriasDto, EmployeeFeriasInput, EmployeeFeriasListItem } from './types';

export function useEmployeeFeriasHooks(employeeId: number): CrudHooks<
  EmployeeFeriasListItem,
  EmployeeFeriasDto,
  EmployeeFeriasInput,
  string
> & {
  api: CrudApi<EmployeeFeriasListItem, EmployeeFeriasDto, EmployeeFeriasInput, string>;
} {
  const feriasApi = useMemo(() => createEmployeeFeriasApi(employeeId), [employeeId]);
  const queryKey = useMemo(() => ['employees', employeeId, 'ferias'] as const, [employeeId]);
  const qc = useQueryClient();

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<EmployeeFeriasListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query],
      queryFn: () => feriasApi.list(query),
      placeholderData: (previous) => previous,
    });

  const useDetail = (
    dataInicio: string | null | undefined,
  ): UseQueryResult<EmployeeFeriasDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', dataInicio],
      queryFn: () => feriasApi.get(dataInicio as string),
      enabled: dataInicio !== null && dataInicio !== undefined && dataInicio !== '',
    });

  const useCreate = (): UseMutationResult<EmployeeFeriasDto, Error, EmployeeFeriasInput> =>
    useMutation({
      mutationFn: (input) => feriasApi.create(input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useUpdate = (): UseMutationResult<
    EmployeeFeriasDto,
    Error,
    { id: string; input: EmployeeFeriasInput }
  > =>
    useMutation({
      mutationFn: ({ id, input }) => feriasApi.update(id, input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useDelete = (): UseMutationResult<void, Error, string> =>
    useMutation({
      mutationFn: (dataInicio) => feriasApi.remove(dataInicio),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  return { api: feriasApi, useList, useDetail, useCreate, useUpdate, useDelete };
}
